import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const LABELS = { experience: "How did you find your Glimpse?", clarity: "Were the items easy to understand?", difficulty: "Any statements difficult to answer?" };

export const GlimpseFeedbackAdmin = () => {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/glimpse/admin/feedback").then((r) => setData(r.data)).catch(() => setData({ total: 0, counts: {}, difficult_items: {}, rows: [] })); }, []);
  const exportCsv = async () => {
    try {
      const r = await api.get("/glimpse/admin/feedback.csv", { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "glimpse-feedback-research.csv"; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed"); }
  };
  if (!data) return null;
  const items = Object.entries(data.difficult_items).sort((x, y) => y[1] - x[1]);
  return (
    <div className="cg-card p-6 space-y-5" data-testid="glimpse-feedback-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><p className="cg-eyebrow">Participant feedback</p>
          <p className="text-sm text-[#9CA3AF] mt-1" data-testid="glimpse-feedback-total">{data.total} response{data.total === 1 ? "" : "s"} · test and demo accounts excluded</p></div>
        <button onClick={exportCsv} data-testid="glimpse-feedback-export" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm border border-[var(--cg-border)] text-[#E8EAEE] hover:border-brass transition-colors">
          <Download className="w-4 h-4" /> Export research CSV</button>
      </div>
      <p className="text-xs text-[#6B7280]">The export uses pseudonymous Glimpse IDs only — no names or email addresses.</p>
      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(data.counts).map(([k, opts]) => (
          <div key={k} className="border border-[var(--cg-border)] rounded-lg p-4">
            <p className="text-sm text-[#F3F4F6] mb-2">{LABELS[k]}</p>
            {Object.entries(opts).map(([o, n]) => <div key={o} className="flex justify-between text-sm text-[#9CA3AF] py-0.5"><span>{o}</span><span className="text-[#F3F4F6]">{n}</span></div>)}
          </div>
        ))}
      </div>
      {items.length > 0 && <p className="text-sm text-[#9CA3AF]" data-testid="glimpse-feedback-difficult-items">Flagged as difficult: {items.map(([c, n]) => `${c} (${n})`).join(", ")}</p>}
      {data.rows.length > 0 && (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-[#9CA3AF] border-b border-[var(--cg-border)]">
            {["Glimpse ID", "Submitted", "Found it", "Clear?", "Difficult?", "Items", "Explanation", "Comments"].map((h) => <th key={h} className="py-2 pr-3 font-normal">{h}</th>)}
          </tr></thead>
          <tbody>{data.rows.slice(0, 50).map((r) => (
            <tr key={r.research_id} className="border-b border-[var(--cg-border)] last:border-0 align-top text-[#E8EAEE]">
              <td className="py-2 pr-3 whitespace-nowrap">{r.research_id}</td>
              <td className="py-2 pr-3 whitespace-nowrap">{r.submitted_at?.slice(0, 10)}</td>
              <td className="py-2 pr-3">{r.experience || "—"}</td><td className="py-2 pr-3">{r.clarity || "—"}</td>
              <td className="py-2 pr-3">{r.difficulty || "—"}</td><td className="py-2 pr-3">{(r.difficult_item_ids || []).join(", ") || "—"}</td>
              <td className="py-2 pr-3 max-w-xs">{r.difficulty_explanation || "—"}</td><td className="py-2 pr-3 max-w-xs">{r.comments || "—"}</td>
            </tr>))}</tbody>
        </table></div>
      )}
    </div>
  );
};
